import { useTranslation } from 'react-i18next';

interface ChallengeItem {
  titleKey: string;
  descKey: string;
}

interface ApproachItem {
  step: string;
  titleKey: string;
  descKey: string;
}

interface ResultItem {
  titleKey: string;
  descKey: string;
}

interface KpiItem {
  valueKey: string;
  labelKey: string;
}

interface CaseBlockProps {
  eyebrowKey: string;
  titleKey: string;
  subtitleKey: string;
  bgColor?: string;
  challenges: ChallengeItem[];
  approaches: ApproachItem[];
  results: ResultItem[];
  kpis: KpiItem[];
}

export default function CaseBlock({
  eyebrowKey,
  titleKey,
  subtitleKey,
  bgColor = '#F7F5F2',
  challenges,
  approaches,
  results,
  kpis,
}: CaseBlockProps) {
  const { t } = useTranslation();

  return (
    <section className="py-20 sm:py-28" style={{ backgroundColor: bgColor }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <p className="text-[#255485] text-xs font-semibold tracking-[0.25em] uppercase mb-4">{t(eyebrowKey)}</p>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t(titleKey)}</h2>
          <p className="text-gray-500 text-sm sm:text-base">{t(subtitleKey)}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-2xl overflow-hidden">
          {/* Challenge */}
          <div className="relative bg-[#1C1C1C] text-white p-8 sm:p-10 flex flex-col">
            <span
              className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none"
              style={{ color: 'rgba(255,255,255,0.04)' }}
            >
              01
            </span>
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.2em] text-red-400 uppercase mb-3">Challenge</p>
              <h3 className="text-2xl font-serif text-white leading-snug" style={{ whiteSpace: 'pre-line' }}>
                {t('page.caseStudy.challenge.title')}
              </h3>
            </div>
            <div className="flex-1 space-y-0">
              {challenges.map((c, i) => (
                <div key={c.titleKey} className={`py-5 ${i !== 0 ? 'border-t border-white/10' : ''}`}>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"></span>
                    <div>
                      <p className="text-sm font-semibold text-white/90 leading-snug mb-1">{t(c.titleKey)}</p>
                      <p className="text-xs text-white/50 leading-relaxed">{t(c.descKey)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approach */}
          <div className="relative bg-[#255485] text-white p-8 sm:p-10 flex flex-col">
            <span
              className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none"
              style={{ color: 'rgba(255,255,255,0.06)' }}
            >
              02
            </span>
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.2em] text-white/60 uppercase mb-3">Approach</p>
              <h3 className="text-2xl font-serif text-white leading-snug" style={{ whiteSpace: 'pre-line' }}>
                {t('page.caseStudy.approach.title')}
              </h3>
            </div>
            <div className="flex-1 space-y-0">
              {approaches.map((a, i) => (
                <div key={a.step} className={`py-5 ${i !== 0 ? 'border-t border-white/15' : ''}`}>
                  <div className="flex items-start gap-4">
                    <span className="flex-shrink-0 text-xs font-black text-white/40 mt-0.5 w-5">{a.step}</span>
                    <div>
                      <p className="text-sm font-semibold text-white leading-snug mb-1">{t(a.titleKey)}</p>
                      <p className="text-xs text-white/60 leading-relaxed">{t(a.descKey)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="relative bg-white p-8 sm:p-10 flex flex-col border border-gray-100">
            <span className="absolute top-4 right-6 text-[10rem] font-black leading-none select-none pointer-events-none text-emerald-50">
              03
            </span>
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-[0.2em] text-emerald-600 uppercase mb-3">Results</p>
              <h3 className="text-2xl font-serif text-gray-900 leading-snug" style={{ whiteSpace: 'pre-line' }}>
                {t('page.caseStudy.results.title')}
              </h3>
            </div>
            <div className="flex gap-3 mb-8">
              {kpis.map((k) => (
                <div key={k.valueKey} className="flex-1 text-center border-t-2 border-emerald-500 pt-3">
                  <p className="text-xl font-black text-emerald-600 leading-none">{t(k.valueKey)}</p>
                  <p className="text-[10px] text-gray-400 mt-1 font-medium">{t(k.labelKey)}</p>
                </div>
              ))}
            </div>
            <div className="flex-1 space-y-0">
              {results.map((r, i) => (
                <div key={r.titleKey} className={`py-5 ${i !== 0 ? 'border-t border-gray-100' : ''}`}>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 leading-snug mb-1">{t(r.titleKey)}</p>
                      <p className="text-xs text-gray-400 leading-relaxed">{t(r.descKey)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
